export type DownloadCategory = "transport" | "rules" | "schedule" | "other";

export interface DownloadLink {
  href: string;
  name: string;
  category: DownloadCategory;
}

export const DOWNLOAD_CATEGORIES = {
  TRANSPORT: "transport" as const,
  RULES: "rules" as const,
  SCHEDULE: "schedule" as const,
  OTHER: "other" as const,
} as const;

const CATEGORY_CONFIG = {
  [DOWNLOAD_CATEGORIES.TRANSPORT]: [
    "bus",
    "transport",
    "route",
    "pickup",
    "drop",
  ],
  [DOWNLOAD_CATEGORIES.RULES]: [
    "rule",
    "regulation",
    "guideline",
    "policy",
    "terms",
    "condition",
  ],
  [DOWNLOAD_CATEGORIES.SCHEDULE]: ["schedule", "calendar", "itinerary"],
} as const;

export const extractDownloadLinks = (htmlContent: string): DownloadLink[] => {
  if (!htmlContent || typeof htmlContent !== "string") {
    return [];
  }

  const links: DownloadLink[] = [];
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, "text/html");
  const anchorTags = doc.querySelectorAll("a[href]");

  anchorTags.forEach((anchor) => {
    const href = anchor.getAttribute("href");
    const name =
      anchor.getAttribute("name") || anchor.textContent?.trim() || "";

    if (!href || !name) return;

    const category = categorizeLink(name);

    links.push({
      href,
      name,
      category,
    });
  });

  return links;
};

const categorizeLink = (name: string): DownloadCategory => {
  const lowerName = name.toLowerCase();

  for (const [category, keywords] of Object.entries(CATEGORY_CONFIG)) {
    if (keywords.some((keyword) => lowerName.includes(keyword))) {
      return category as DownloadCategory;
    }
  }

  return DOWNLOAD_CATEGORIES.OTHER;
};

// export const getLinksByCategory = (
//   links: DownloadLink[],
//   category: DownloadCategory
// ): DownloadLink[] => {
//   return links.filter(link => link.category === category);
// };

export const getFirstLinkByCategory = (
  links: DownloadLink[],
  category: DownloadCategory,
): DownloadLink | null => {
  return links.find((link) => link.category === category) || null;
};

export const getLinksByCategories = (
  links: DownloadLink[],
): Record<DownloadCategory, DownloadLink[]> => {
  const categorized: Record<string, DownloadLink[]> = {
    [DOWNLOAD_CATEGORIES.TRANSPORT]: [],
    [DOWNLOAD_CATEGORIES.RULES]: [],
    [DOWNLOAD_CATEGORIES.SCHEDULE]: [],
    [DOWNLOAD_CATEGORIES.OTHER]: [],
  };

  links.forEach((link) => {
    if (categorized[link.category]) {
      categorized[link.category].push(link);
    }
  });

  return categorized as Record<DownloadCategory, DownloadLink[]>;
};

export const getCategorizedLinks = (links: DownloadLink[]) => {
  return {
    transport: getFirstLinkByCategory(links, DOWNLOAD_CATEGORIES.TRANSPORT),
    rules: getFirstLinkByCategory(links, DOWNLOAD_CATEGORIES.RULES),
    schedule: getFirstLinkByCategory(links, DOWNLOAD_CATEGORIES.SCHEDULE),
    all: getLinksByCategories(links),
  };
};
